// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import styles from './QrBarcodeScanner.module.scss';
import { mergeClasses } from 'util/commons';
import IconWarning from '../icons/IconWarning';
import { useTranslation } from 'react-i18next';

type QrBarcodeScannerProps = {
  classNames?: {
    container?: string;
    video?: string;
  };
  onScanSuccess?: (decodedText: string) => void;
  paused?: boolean;
};

const QrBarcodeScanner: React.FC<QrBarcodeScannerProps> = ({
  classNames,
  onScanSuccess,
  paused = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader>(
    new BrowserMultiFormatReader()
  );
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<
    'failed_to_scan' | 'failed_to_start_camera'
  >();
  const { t } = useTranslation();
  const lastScannedCodeRef = useRef<string>('');
  const lastScanTimeRef = useRef<number>(0);
  const [active, setActive] = useState(true);
  const [showStartButton, setShowStartButton] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const hasMountedRef = useRef(false);

  const handleScanSuccess = useCallback(
    (decodedText: string) => {
      const now = Date.now();
      const timeDiff = now - lastScanTimeRef.current;

      // Prevent duplicate scans of the same code within 2 seconds
      if (lastScannedCodeRef.current === decodedText && timeDiff < 2000) {
        return;
      }

      lastScannedCodeRef.current = decodedText;
      lastScanTimeRef.current = now;
      onScanSuccess?.(decodedText);
    },
    [onScanSuccess]
  );

  const handleStartScan = async () => {
    setError(undefined);
    setIsInitializing(true);

    try {
      if (!videoRef.current) {
        return;
      }

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      streamRef.current = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      videoRef.current.srcObject = streamRef.current;

      setShowStartButton(false);

      codeReader.current.decodeFromStream(
        streamRef.current,
        videoRef.current,
        (result, err) => {
          if (!active) {
            return;
          }

          if (result) {
            handleScanSuccess(result.getText());
            return;
          }

          if (err && !(err instanceof NotFoundException)) {
            setError('failed_to_scan');
          }
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.message === 'Permission denied') {
        setPermissionDenied(true);

        return;
      }

      if (error?.message?.includes('denied permission')) {
        setShowStartButton(true);

        return;
      }

      setError('failed_to_start_camera');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    handleStartScan();

    return () => {
      setActive(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      codeReader.current.reset();

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (paused) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      codeReader.current.reset();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } else {
      handleStartScan();
    }
  }, [paused]);

  useEffect(() => {
    if (error) {
      toast.error(t(`common_scan.${error}`));
      setError(undefined);
    }
  }, [error, t]);

  return (
    <div
      className={mergeClasses(styles.ScannerContainer, classNames?.container)}
    >
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        controls={false}
        className={mergeClasses(styles.ScannerVideo, classNames?.video)}
      />

      {isInitializing && !permissionDenied && (
        <div
          className={mergeClasses(styles.Overlay, styles.InitializingOverlay)}
        >
          <p>{t('common_scan.initializing_camera')}</p>
        </div>
      )}

      {permissionDenied && (
        <div className={mergeClasses(styles.Overlay, styles.PermissionDenied)}>
          <IconWarning width={32} height={32} />

          <p>{t('common_scan.permission_denied')}</p>
        </div>
      )}

      {showStartButton && !isInitializing && !permissionDenied && (
        <div className={styles.Overlay}>
          <button className={styles.StartButton} onClick={handleStartScan}>
            {t('common_scan.start_camera')}
          </button>
        </div>
      )}
    </div>
  );
};

export default QrBarcodeScanner;
